// src/components/pages/machinery/Compactadoras.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Compactadoras_1,
    Compactadoras_2,
    Compactadoras_3,
    Compactadoras_4,
    Compactadoras_5,
    Compactadoras_6
} from '../../../assets/images';
import { useCart } from '../../../context/CartContext';
import { jcb } from '../../../api/apiClient';

const SLUG = 'vibrocompactador';
const MACHINERY_TYPE = 'VIBROCOMPACTADOR';

const Compactadoras = () => {
    const { addMachinery, cartItems } = useCart();
    const navigate = useNavigate();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const [models, setModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(true);

    const images = [
        Compactadoras_1,
        Compactadoras_2,
        Compactadoras_3,
        Compactadoras_4,
        Compactadoras_5,
        Compactadoras_6
    ];

    useEffect(() => {
        let isMounted = true;
        const loadModels = async () => {
            try {
                const { data } = await jcb.get(`/machinery/type/${SLUG}`);
                if (!isMounted) return;
                const mapped = (data.models || []).map((m) => ({
                    id: m.id,
                    name: m.name,
                    fullName: m.full_name || m.name,
                    weight: m.specs?.operating_weight || m.specs?.weight || '—',
                    power: m.specs?.engine_power || '—',
                    drumWidth: m.specs?.dimensions || '—',
                    frequency: '—',
                    centrifugalForce: '—',
                    photo: m.photo,
                }));
                setModels(mapped);
            } catch (err) {
                console.error('Error cargando modelos:', err);
            } finally {
                if (isMounted) setLoadingModels(false);
            }
        };
        loadModels();
        return () => { isMounted = false; };
    }, []);

    const nextImage = () => setCurrentImageIndex((p) => p === images.length - 1 ? 0 : p + 1);
    const prevImage = () => setCurrentImageIndex((p) => p === 0 ? images.length - 1 : p - 1);

    const features = [
        { title: 'Calidad, Rendimiento y Durabilidad', description: 'Los rodillos de compactación de JCB trabajan de manera rápida, potente y precisa, ofreciéndole un control total y manteniendo unos niveles de productividad imbatibles.' },
        { title: 'Fáciles de Usar', description: 'Nuestros modelos VM75, VM166 y VM200 cuentan con el mismo sistema de operación intuitivo y fácil de usar, que le permite ponerse a trabajar de forma rápida y sencilla.' },
        { title: 'Facilidad de Servicio', description: 'Todas nuestras compactadoras de tierras están diseñadas teniendo siempre en cuenta la facilidad de servicio, lo que las hace fáciles de mantener para disfrutar de un mayor periodo de actividad.' },
        { title: 'Cabina de Alto Confort', description: 'Cabina con certificación ROPS/FOPS, bastidor ROPS, luces de trabajo, luces de carretera y adaptador para asiento giratorio.' },
        { title: 'Monitoreo Completo', description: 'Contador de horas, combustible, temperatura del motor, presión del aceite, corriente de carga de batería, estado del filtro de aire y aceite hidráulico.' },
        { title: 'Seguridad y Control', description: 'Freno de estacionamiento, palanca de control de punto muerto, selección de rango de velocidad, control automático de vibración y alarma acústica de marcha atrás.' }
    ];

    const buildModelCartItem = (model) => ({
        id: `machinery-${model.id}`,
        name: model.name,
        price: 0,
        machineryId: model.id,
        machineryType: MACHINERY_TYPE,
        model: model.name,
    });

    const buildGenericCartItem = () => ({
        id: `machinery-type-${SLUG}`,
        name: null,
        price: 0,
        machineryId: null,
        machineryType: MACHINERY_TYPE,
        model: null,
    });

    const handleAddToCart = (model) => addMachinery(buildModelCartItem(model));
    const handleAddAndGoToContact = (model) => {
        addMachinery(buildModelCartItem(model));
        navigate('/contact');
    };
    const handleAddGeneric = () => addMachinery(buildGenericCartItem());
    const handleAddGenericAndGoToContact = () => {
        handleAddGeneric();
        navigate('/contact');
    };
    const handleCtaQuote = () => handleAddGenericAndGoToContact();

    const isInCart = (id) =>
        cartItems.some(item => item.itemType === 'machinery' && item.id === `machinery-${id}`);

    const machineryCount = cartItems.filter(i => i.itemType === 'machinery').length;
    const ctaDisabled = loadingModels;
    const showModelsSection = !loadingModels && models.length > 0;

    return (
        <div className="container mx-auto px-6 py-24">
            <nav className="text-sm mb-8">
                <Link to="/" className="text-text-muted hover:text-dark-primary">Inicio</Link>
                <span className="mx-2 text-text-muted">/</span>
                <Link to="/machinery" className="text-text-muted hover:text-dark-primary">Maquinaria</Link>
                <span className="mx-2 text-text-muted">/</span>
                <span className="text-dark-primary font-medium">Compactadoras</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
                <div>
                    <h1 className="text-4xl md:text-5xl font-display font-bold text-dark-primary mb-2">
                        Equipos de Compactación
                    </h1>
                    <p className="text-text-muted text-lg mb-6">
                        Los rodillos compactadores vibratorios en tándem de JCB están diseñados para ser líderes en su clase.
                        Con un peso de entre 1680 y 9250 kg, nuestro equipo de compactación está desarrollado para brindar
                        confiabilidad, bajo costo de operación, seguridad, facilidad de servicio, maniobrabilidad, rendimiento,
                        comodidad y la mejor durabilidad de su clase.
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <button onClick={handleCtaQuote} disabled={ctaDisabled} className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium bg-jcb-yellow text-dark-primary hover:bg-yellow-500 px-8 py-3 rounded-none transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                            Solicitar información
                        </button>
                    </div>
                </div>
                <div className="relative">
                    <div className="bg-surface-subtle aspect-video rounded-lg flex items-center justify-center overflow-hidden relative">
                        <img src={images[currentImageIndex]} alt={`Compactadora - Imagen ${currentImageIndex + 1}`} className="w-full h-full object-cover transition-opacity duration-300" />
                        {images.length > 1 && (
                            <>
                                <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors" aria-label="Imagen anterior">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors" aria-label="Imagen siguiente">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </button>
                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                                    {images.map((_, index) => (
                                        <button key={index} onClick={() => setCurrentImageIndex(index)} className={`w-2 h-2 rounded-full transition-all ${currentImageIndex === index ? 'bg-white w-4' : 'bg-white/50'}`} aria-label={`Ir a imagen ${index + 1}`} />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    {images.length > 1 && (
                        <p className="text-center text-sm text-text-muted mt-2">{currentImageIndex + 1} / {images.length}</p>
                    )}
                </div>
            </div>

            <div className="mb-16">
                <h2 className="text-2xl font-display font-bold text-dark-primary mb-8">Características Destacadas</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, index) => (
                        <div key={index} className="p-6 bg-white border border-border-subtle hover:shadow-md transition-shadow">
                            <h3 className="font-display font-semibold text-dark-primary text-lg mb-2">{feature.title}</h3>
                            <p className="text-text-muted text-sm">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {showModelsSection && (
                <div className="mb-16 overflow-x-auto">
                    <h2 className="text-2xl font-display font-bold text-dark-primary mb-8">Comparativa de Modelos</h2>

                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-dark-primary text-white">
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Modelo</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Peso de operación</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Potencia</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Ancho de tambor</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Frecuencia</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Fuerza centrífuga</th>
                                <th className="px-6 py-4 text-left font-display text-xs uppercase tracking-luxury">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {models.map((model) => {
                                const inCart = isInCart(model.id);
                                return (
                                    <tr key={model.id} className="border-b border-border-subtle hover:bg-surface-subtle">
                                        <td className="px-6 py-4 font-display font-semibold text-dark-primary">{model.name}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.weight}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.power}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.drumWidth}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.frequency}</td>
                                        <td className="px-6 py-4 text-text-muted">{model.centrifugalForce}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <button onClick={() => handleAddToCart(model)} className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap">
                                                    {inCart ? 'Agregar +1' : 'Agregar a cotización'}
                                                </button>
                                                <span className="text-border-subtle hidden sm:inline">|</span>
                                                {inCart ? (
                                                    <Link to="/cart" className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap">
                                                        Ver carrito
                                                    </Link>
                                                ) : (
                                                    <button onClick={() => handleAddAndGoToContact(model)} className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors whitespace-nowrap">
                                                        Cotizar
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {machineryCount > 0 && (
                        <div className="mt-4 flex justify-end">
                            <Link to="/cart" className="font-display text-xs uppercase tracking-luxury font-medium bg-dark-primary text-white hover:bg-black px-6 py-3 transition-all">
                                Ir a cotizar ({machineryCount} {machineryCount === 1 ? 'equipo' : 'equipos'})
                            </Link>
                        </div>
                    )}
                </div>
            )}

            <div className="bg-surface-subtle p-8 md:p-12 text-center">
                <h2 className="text-2xl font-display font-bold text-dark-primary mb-4">¿Interesado en las Compactadoras?</h2>
                <p className="text-text-muted mb-6 max-w-2xl mx-auto">
                    Contáctanos para obtener más información, cotizaciones o asesoría técnica sobre nuestros modelos.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                    <Link to="/contact" className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium border border-border-medium text-text-muted hover:text-dark-primary hover:border-dark-primary px-8 py-3 transition-all duration-300">
                        Contactar
                    </Link>
                    <button onClick={handleCtaQuote} disabled={ctaDisabled} className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium bg-jcb-yellow text-dark-primary hover:bg-yellow-500 px-8 py-3 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                        Solicitar información
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Compactadoras;